import type { CSSProperties, ReactNode } from "react";

// Stroke-based 24x24 icon set ported from the EditsForge design system.
interface IcoProps {
  size?: number;
  className?: string;
  style?: CSSProperties;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  viewBox?: string;
  children?: ReactNode;
}

function Ico({
  size = 16,
  className = "",
  style,
  fill = "none",
  stroke = "currentColor",
  strokeWidth = 1.5,
  viewBox = "0 0 24 24",
  children,
}: IcoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`block shrink-0 ${className}`}
      style={style}
      aria-hidden
    >
      {children}
    </svg>
  );
}

type P = Omit<IcoProps, "children" | "viewBox">;

export const IcoPlay = (p: P) => (
  <Ico {...p} fill="currentColor" stroke="none">
    <path d="M5 3l14 9-14 9V3z" />
  </Ico>
);
export const IcoPlus = (p: P) => (
  <Ico {...p}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </Ico>
);
export const IcoTrash = (p: P) => (
  <Ico {...p}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </Ico>
);
export const IcoFilm = (p: P) => (
  <Ico {...p}>
    <rect x="2" y="7" width="20" height="15" rx="2" />
    <path d="M17 2l-5 5-5-5" />
    <line x1="2" y1="12" x2="22" y2="12" />
  </Ico>
);
export const IcoText = (p: P) => (
  <Ico {...p}>
    <path d="M4 7V4h16v3" />
    <path d="M9 20h6" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </Ico>
);
export const IcoExport = (p: P) => (
  <Ico {...p}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </Ico>
);
export const IcoUpload = (p: P) => (
  <Ico {...p}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </Ico>
);
export const IcoSparkle = (p: P) => (
  <Ico {...p}>
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
  </Ico>
);
export const IcoSettings = (p: P) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </Ico>
);
export const IcoUser = (p: P) => (
  <Ico {...p}>
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </Ico>
);
export const IcoSearch = (p: P) => (
  <Ico {...p}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </Ico>
);
export const IcoBolt = (p: P) => (
  <Ico {...p} fill="currentColor" stroke="none">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </Ico>
);
export const IcoBell = (p: P) => (
  <Ico {...p}>
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </Ico>
);
export const IcoCheck = (p: P) => (
  <Ico {...p}>
    <polyline points="20 6 9 17 4 12" />
  </Ico>
);
export const IcoX = (p: P) => (
  <Ico {...p}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Ico>
);
export const IcoCard = (p: P) => (
  <Ico {...p}>
    <rect x="1" y="4" width="22" height="16" rx="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </Ico>
);
export const IcoMore = (p: P) => (
  <Ico {...p} fill="currentColor" stroke="none">
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </Ico>
);
export const IcoGrid = (p: P) => (
  <Ico {...p}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </Ico>
);
export const IcoList = (p: P) => (
  <Ico {...p}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </Ico>
);
export const IcoClock = (p: P) => (
  <Ico {...p}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </Ico>
);
export const IcoLayers = (p: P) => (
  <Ico {...p}>
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </Ico>
);
export const IcoEye = (p: P) => (
  <Ico {...p}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </Ico>
);
export const IcoCpu = (p: P) => (
  <Ico {...p}>
    <rect x="7" y="7" width="10" height="10" rx="1" />
    <path d="M9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4" />
  </Ico>
);
export const IcoLink = (p: P) => (
  <Ico {...p}>
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </Ico>
);
export const IcoLock = (p: P) => (
  <Ico {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </Ico>
);
export const IcoGitHub = (p: P) => (
  <Ico {...p}>
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22" />
  </Ico>
);
export const IcoWand = (p: P) => (
  <Ico {...p}>
    <path d="M15 4l5 5L8 21H3v-5L15 4z" />
    <path d="M14 5l5 5" />
  </Ico>
);
export const IcoUndo = (p: P) => (
  <Ico {...p}>
    <path d="M3 7v6h6" />
    <path d="M3.5 13A9 9 0 1021 12" />
  </Ico>
);
export const IcoRedo = (p: P) => (
  <Ico {...p}>
    <path d="M21 7v6h-6" />
    <path d="M20.5 13A9 9 0 113 12" />
  </Ico>
);
export const IcoBack = (p: P) => (
  <Ico {...p}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </Ico>
);
export const IcoSplit = (p: P) => (
  <Ico {...p}>
    <line x1="12" y1="3" x2="12" y2="21" />
    <path d="M7 8l-4 3 4 3" />
    <path d="M17 8l4 3-4 3" />
  </Ico>
);
export const IcoMusic = (p: P) => (
  <Ico {...p}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="21" cy="16" r="3" />
  </Ico>
);
export const IcoVolume = (p: P) => (
  <Ico {...p}>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 010 14.14" />
    <path d="M15.54 8.46a5 5 0 010 7.07" />
  </Ico>
);
export const IcoMaximize = (p: P) => (
  <Ico {...p}>
    <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />
  </Ico>
);
export const IcoChevronRight = (p: P) => (
  <Ico {...p}>
    <polyline points="9 18 15 12 9 6" />
  </Ico>
);
export const IcoGoogle = (p: P) => (
  <Ico {...p} fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </Ico>
);
